import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Cluster, Drawer, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../../lib/saveNotice";
import { opsApi, type DocumentTemplate } from "../../../lib/opsApi";
import { blobToDataUrl } from "../../../lib/blobDataUrl";
import { EditInput, FieldPicker, PositionGrid } from "../dashboard_designer/DesignerControls";
import { FIELD_HINTS } from "../../../help/fieldHints";
import { useLocale } from "../../../shell/LocaleProvider";
import "../dashboard_designer/designers.css";
import "../dashboard_designer/dashboard_designer.css";
import "./document_designer.css";
import {
  BLOCK_PALETTE,
  CALC_PALETTE,
  DOCUMENT_FIELDS,
  FIELD_GROUP_LABELS,
  FIELD_GROUP_ORDER,
  MONEY_FIELDS,
  UNIQUE_BLOCK_TYPES,
  applyTemplateEffects,
  blockLabel,
  elementFromField,
  elementFromPalette,
  elementFromPreset,
  insertRow,
  maxRow,
  money,
  placementStyle,
  samplePayload,
  type DocumentElement,
  type DocumentSample,
} from "./model";

export function DocumentDesignerPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [documents, setDocuments] = useState<DocumentTemplate[]>([]);
  const [active, setActive] = useState<DocumentTemplate | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fieldSearch, setFieldSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set(["amounts"]));
  const [logoSrc, setLogoSrc] = useState("");

  const load = useCallback(async () => {
    try {
      const result = await opsApi.listDocuments(orgId);
      setDocuments(result.documents);
      setActive((current) => result.documents.find((doc) => doc.id === current?.id) ?? result.documents[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Templates could not be loaded.");
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    setLogoSrc("");
    void opsApi.getDocumentLogo(orgId)
      .then(async (blob) => {
        if (!blob || blob.size === 0) {
          if (!cancelled) setLogoSrc("");
          return;
        }
        const preview = await blobToDataUrl(blob);
        if (!cancelled) setLogoSrc(preview);
      })
      .catch(() => {
        if (!cancelled) setLogoSrc("");
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const elements = active?.elements ?? [];
  const sample = useMemo(
    () => applyTemplateEffects(elements, samplePayload(active?.kind ?? "quote")),
    [active?.kind, elements],
  );
  const editing = elements.find((element) => element.id === editingId);

  function patch(fn: (doc: DocumentTemplate) => DocumentTemplate) {
    setActive((prev) => (prev ? fn(prev) : prev));
  }

  function patchElement(id: string, fn: (element: DocumentElement) => DocumentElement) {
    patch((doc) => ({ ...doc, elements: doc.elements.map((element) => (element.id === id ? fn(element) : element)) }));
  }

  function addElement(element: DocumentElement, row: number) {
    patch((doc) => ({
      ...doc,
      elements: [...insertRow(doc.elements, row), { ...element, canvas_row: row }],
    }));
    setEditingId(element.id);
  }

  function addBlock(type: string) {
    if (!active) return;
    if (UNIQUE_BLOCK_TYPES.has(type)) {
      const existing = elements.find((element) => element.type === type);
      if (existing) {
        setEditingId(existing.id);
        return;
      }
    }
    addElement(elementFromPalette(type, active.kind, maxRow(elements) + 1), maxRow(elements) + 1);
  }

  function addPreset(preset: string) {
    if (!active) return;
    const host = preset.startsWith("delivery")
      ? elements.find((element) => element.type === "lines")
      : elements.find((element) => element.type === "totals");
    const row = (host?.canvas_row ?? maxRow(elements)) + 1;
    addElement(elementFromPreset(preset, row), row);
  }

  function addField(fieldKey: string) {
    addElement(elementFromField(fieldKey, maxRow(elements) + 1), maxRow(elements) + 1);
  }

  function removeElement(id: string) {
    patch((doc) => ({ ...doc, elements: doc.elements.filter((element) => element.id !== id) }));
    setEditingId((current) => (current === id ? null : current));
  }

  function moveElement(id: string, direction: -1 | 1) {
    const sorted = [...elements].sort((a, b) => a.canvas_row - b.canvas_row || a.col_start - b.col_start);
    const index = sorted.findIndex((element) => element.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= sorted.length) return;
    const current = sorted[index];
    const neighbor = sorted[target];
    patch((doc) => ({
      ...doc,
      elements: doc.elements.map((element) => {
        if (element.id === current.id) return { ...element, canvas_row: neighbor.canvas_row };
        if (element.id === neighbor.id) return { ...element, canvas_row: current.canvas_row };
        return element;
      }),
    }));
  }

  async function save() {
    if (!active) return;
    setSaving(true);
    setError("");
    try {
      const saved = await opsApi.saveDocument(orgId, {
        id: active.id,
        kind: active.kind,
        name: active.name,
        page_size: active.page_size,
        elements: active.elements,
      });
      setActive(saved);
      setDocuments((current) => current.map((doc) => (doc.id === saved.id ? saved : doc)));
      reportSaved(show, "Template saved.");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Template could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  const searchTerm = fieldSearch.trim().toLowerCase();
  const visibleFields = searchTerm
    ? DOCUMENT_FIELDS.filter((field) => field.label.toLowerCase().includes(searchTerm) || field.key.toLowerCase().includes(searchTerm))
    : DOCUMENT_FIELDS;
  const fieldsByGroup = FIELD_GROUP_ORDER.map((group) => ({
    group,
    fields: visibleFields.filter((field) => field.group === group),
  })).filter((entry) => entry.fields.length > 0);

  function toggleGroup(group: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  if (!active) {
    return <p>{error || "Loading templates…"}</p>;
  }

  const sorted = [...elements].sort((a, b) => a.canvas_row - b.canvas_row || a.col_start - b.col_start);

  return (
    <div className="designer">
      <Cluster justify="space-between" align="center">
        <h1>{`Designing: ${active.name}`}</h1>
        <Button disabled={saving} onClick={() => save()}>{saving ? "Saving…" : "Save"}</Button>
      </Cluster>
      {error ? <p role="alert" className="solution-error">{error}</p> : null}

      <div className="document-designer__kind-tabs" role="tablist" aria-label="Document templates">
        {documents.map((doc) => (
          <Button
            key={doc.id}
            variant={active.id === doc.id ? "primary" : "secondary"}
            onClick={() => {
              setActive(doc);
              setEditingId(null);
            }}
          >
            {doc.kind}: {doc.name}
          </Button>
        ))}
      </div>

      <div className="dashboard-designer__layout">
        <aside className="dashboard-designer__sidebar">
          <div className="dashboard-designer__palette">
            <h2>Add block</h2>
            {BLOCK_PALETTE.map((entry) => (
              <button
                key={entry.type}
                type="button"
                className="dashboard-designer__palette-item"
                onClick={() => addBlock(entry.type)}
              >
                <span className="dashboard-designer__palette-item-label">{entry.label}</span>
                <span className="dashboard-designer__palette-item-description">{entry.description}</span>
              </button>
            ))}
          </div>

          <div className="dashboard-designer__palette">
            <h2>Calculations</h2>
            {CALC_PALETTE.map((entry) => (
              <button
                key={entry.preset}
                type="button"
                className="dashboard-designer__palette-item"
                onClick={() => addPreset(entry.preset)}
              >
                <span className="dashboard-designer__palette-item-label">{entry.label}</span>
                <span className="dashboard-designer__palette-item-description">{entry.description}</span>
              </button>
            ))}
          </div>

          <div className="dashboard-designer__source">
            <h2>Fields</h2>
            <div className="dashboard-designer__source-name">{active.kind} document</div>
            <div className="dashboard-designer__source-count">{DOCUMENT_FIELDS.length} fields</div>
            <input
              type="search"
              className="dashboard-designer__source-search"
              placeholder="Search fields…"
              value={fieldSearch}
              onChange={(event) => setFieldSearch(event.target.value)}
            />
            <div className="dashboard-designer__source-scroll">
              {fieldsByGroup.length === 0 ? (
                <p className="dashboard-designer__source-empty">No fields match "{fieldSearch}".</p>
              ) : fieldsByGroup.map(({ group, fields }) => {
                const collapsed = !searchTerm && collapsedGroups.has(group);
                return (
                  <div key={group} className="dashboard-designer__source-group">
                    <button type="button" className="dashboard-designer__source-group-header" onClick={() => toggleGroup(group)}>
                      <span>{FIELD_GROUP_LABELS[group]} <span className="dashboard-designer__source-group-count">({fields.length})</span></span>
                      <span>{collapsed ? "▸" : "▾"}</span>
                    </button>
                    {!collapsed && (
                      <ul className="dashboard-designer__source-fields">
                        {fields.map((field) => (
                          <li key={field.key}>
                            <button
                              type="button"
                              className="dashboard-designer__source-field"
                              onClick={() => addField(field.key)}
                            >
                              <span>{field.label}</span>
                              <span className="dashboard-designer__source-field-kind">{field.kind}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        <div className="dashboard-designer__canvas">
          <section className="designer-guidance">
            <EditInput label="Template name" value={active.name} onChange={(name) => patch((doc) => ({ ...doc, name }))} />
            <EditInput label="Page size" tooltip={FIELD_HINTS.document_page_size} value={active.page_size} options={["A4", "Letter"]} onChange={(page_size) => patch((doc) => ({ ...doc, page_size }))} />
          </section>

          <div className="designer-position-canvas document-designer__page" aria-label="Document page">
            {sorted.length === 0 ? (
              <p className="dashboard-designer__empty-canvas" style={{ gridColumn: "1 / span 7" }}>
                Add a block from the palette on the left to start building this {active.kind}.
              </p>
            ) : sorted.map((element, index) => (
              <div
                key={element.id}
                className={`designer-position-element${editingId === element.id ? " selected" : ""}`}
                style={placementStyle(element)}
              >
                <div className="document-designer__tile-toolbar">
                  <span className="dashboard-designer__item-summary">{blockLabel(element)}</span>
                  <Cluster gap={1}>
                    <Button variant="secondary" disabled={index === 0} onClick={() => moveElement(element.id, -1)}>▲</Button>
                    <Button variant="secondary" disabled={index === sorted.length - 1} onClick={() => moveElement(element.id, 1)}>▼</Button>
                    <Button variant="secondary" onClick={() => setEditingId(element.id)}>Configure</Button>
                    <Button variant="destructive" onClick={() => removeElement(element.id)}>Delete</Button>
                  </Cluster>
                </div>
                <div className="document-designer__tile-preview">
                  <ElementPreview element={element} sample={sample} kind={active.kind} logoSrc={logoSrc} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Drawer
        className="designer"
        open={Boolean(editing)}
        onClose={() => setEditingId(null)}
        title={editing ? `Configure: ${blockLabel(editing)}` : "Configure"}
        footer={<Button onClick={() => setEditingId(null)}>Done</Button>}
      >
        {editing && (
          <ElementConfigForm
            element={editing}
            kind={active.kind}
            logoSrc={logoSrc}
            onChange={(fn) => patchElement(editing.id, fn)}
          />
        )}
      </Drawer>
    </div>
  );
}

function ElementPreview({ element, sample, kind, logoSrc }: {
  element: DocumentElement;
  sample: DocumentSample;
  kind: string;
  logoSrc: string;
}) {
  const { formatDate, numberFormat } = useLocale();
  const showMoney = (value: unknown, currency = "") => money(value, currency, numberFormat);
  const cfg = element.config ?? {};
  if (element.type === "logo") {
    return (
      <>
        {logoSrc ? <img className="document-designer__logo" src={logoSrc} alt={sample.seller_name} /> : null}
        <p className="document-designer__kicker">{sample.kind} · {cfg.label || "Logo"}</p>
      </>
    );
  }
  if (element.type === "company") {
    const address = sample.seller_address_lines?.length
      ? sample.seller_address_lines
      : sample.seller_address ? sample.seller_address.split("\n") : [];
    return (
      <>
        <p>{sample.seller_name}</p>
        {address.map((line, index) => <p key={`seller-${index}`}>{line}</p>)}
        {sample.seller_tax_number ? <p>{sample.seller_tax_number}</p> : null}
      </>
    );
  }
  if (element.type === "customer") {
    const address = sample.customer_address_lines?.length
      ? sample.customer_address_lines
      : sample.customer_address ? sample.customer_address.split("\n") : [];
    return (
      <>
        <p>{sample.customer_name}</p>
        {address.map((line, index) => <p key={`customer-${index}`}>{line}</p>)}
      </>
    );
  }
  if (element.type === "number") {
    const dateLabel = kind === "order" ? "Date of order" : kind === "invoice" ? "Date of invoice" : "Date of quote";
    return (
      <>
        <p className="document-letterhead__label">{cfg.label || `${kind} number`}</p>
        <p>{sample.number}</p>
        {sample.issue_date ? <p>{dateLabel}: {formatDate(sample.issue_date)}</p> : null}
      </>
    );
  }
  if (element.type === "lines") {
    const rows = (sample.line_items ?? []).filter((item) => !item.template);
    return (
      <>
        <h2>{cfg.label || "Line items"}</h2>
        <table className="document-lines">
          <thead><tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Discount</th><th>Amount</th></tr></thead>
          <tbody>
            {rows.map((item, index) => (
              <tr key={`${item.description}-${index}`}>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{showMoney(item.unit_price)}</td>
                <td>{item.discount_pct ?? 0}%</td>
                <td>{showMoney(linePreviewAmount(item))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }
  if (element.type === "extra_line") {
    const qty = Number(cfg.quantity ?? 1);
    const unit = cfg.op === "percent"
      ? Number(sample.subtotal) * Number(cfg.rate ?? 0) / 100 / (qty || 1)
      : Number(cfg.unit_price ?? cfg.amount ?? 0);
    return (
      <>
        <h2>{cfg.label || "Extra line"}</h2>
        <table className="document-lines">
          <tbody>
            <tr>
              <td>{cfg.description || cfg.label || "Item"}</td>
              <td>{qty}</td>
              <td>{showMoney(unit)}</td>
              <td>0%</td>
              <td>{showMoney(qty * unit)}</td>
            </tr>
          </tbody>
        </table>
      </>
    );
  }
  if (element.type === "totals") {
    const currency = sample.currency;
    const calcApply = new Set((sample.calc_rows ?? []).map((row) => row.apply_as));
    return (
      <div className="document-totals">
        <div className="document-total-row"><span>Subtotal (before discount)</span><span>{showMoney(sample.subtotal, currency)}</span></div>
        {!calcApply.has("discount") ? <div className="document-total-row"><span>Discount</span><span>{showMoney(sample.discount_total, currency)}</span></div> : null}
        {(!calcApply.has("tax") && (Number(sample.tax_rate) > 0 || Number(sample.tax_total) !== 0)) ? (
          <div className="document-total-row">
            <span>{Number(sample.tax_rate) > 0 ? `Tax (${Number(sample.tax_rate)}%)` : "Tax"}</span>
            <span>{showMoney(sample.tax_total, currency)}</span>
          </div>
        ) : null}
        {(sample.calc_rows ?? []).filter((row) => row.apply_as === "surcharge" || row.apply_as === "display").map((row) => (
          <div key={row.label} className="document-total-row"><span>{row.label}</span><span>{showMoney(row.amount, currency)}</span></div>
        ))}
        <div className="document-total-row is-grand"><span>Total</span><span>{showMoney(sample.total, currency)}</span></div>
      </div>
    );
  }
  if (element.type === "calc") {
    const row = (sample.calc_rows ?? []).find((item) => item.label === cfg.label);
    return (
      <div className="document-totals">
        <div className="document-total-row">
          <span>{cfg.label || "Calculation"}</span>
          <span>{showMoney(row?.amount ?? 0, sample.currency)}</span>
        </div>
      </div>
    );
  }
  if (element.type === "terms") {
    return (
      <>
        <h2>{cfg.label || "Terms"}</h2>
        <p>{sample.terms || "Standard terms apply."}</p>
      </>
    );
  }
  if (element.type === "signature") {
    return (
      <>
        <h2>{cfg.label || "Signature"}</h2>
        <p className="document-sign-line">Signed: ____________________________</p>
        <p className="document-sign-line">Date: ____________</p>
      </>
    );
  }
  if (element.type === "field") {
    const key = cfg.field_key ?? "";
    const raw = (sample as unknown as Record<string, unknown>)[key];
    const meta = DOCUMENT_FIELDS.find((item) => item.key === key);
    const value = MONEY_FIELDS.has(key)
      ? showMoney(raw, sample.currency)
      : meta?.kind === "date" && raw
        ? formatDate(String(raw))
        : String(raw ?? "");
    return (
      <>
        <h2>{cfg.label || key}</h2>
        <p>{value}</p>
      </>
    );
  }
  return (
    <>
      <h2>{cfg.label || "Text"}</h2>
      <p>{cfg.text || "Empty text box"}</p>
    </>
  );
}

function linePreviewAmount(item: DocumentSample["line_items"][number]): number {
  const raw = Number(item.quantity) * Number(item.unit_price);
  return raw - raw * (Number(item.discount_pct ?? 0) / 100);
}

function ElementConfigForm({ element, kind, logoSrc, onChange }: {
  element: DocumentElement;
  kind: string;
  logoSrc: string;
  onChange: (fn: (element: DocumentElement) => DocumentElement) => void;
}) {
  const cfg = element.config ?? {};
  function setConfig(patch: Partial<DocumentElement["config"]>) {
    onChange((current) => ({ ...current, config: { ...current.config, ...patch } }));
  }
  return (
    <>
      <EditInput label="Label" tooltip={FIELD_HINTS.document_block_label} value={cfg.label || ""} onChange={(label) => setConfig({ label })} />
      {element.type === "logo" && (
        <div className="document-designer__logo-config">
          <div className="document-designer__logo-frame">
            {logoSrc ? (
              <img className="document-designer__logo" src={logoSrc} alt="" />
            ) : (
              <p>No logo uploaded yet</p>
            )}
          </div>
          <p className="document-designer__logo-note">Upload logo in org setting</p>
        </div>
      )}
      {element.type === "field" && (
        <FieldPicker
          label="Field"
          tooltip={FIELD_HINTS.document_field_picker}
          value={cfg.field_key ?? ""}
          options={DOCUMENT_FIELDS.map((field) => ({ value: field.key, label: field.label }))}
          onChange={(field_key) => setConfig({ field_key, label: DOCUMENT_FIELDS.find((field) => field.key === field_key)?.label || cfg.label })}
        />
      )}
      {element.type === "text" && (
        <EditInput label="Text" type="textarea" value={cfg.text || ""} onChange={(text) => setConfig({ text })} />
      )}
      {element.type === "calc" && (
        <>
          <EditInput label="Operation" tooltip={FIELD_HINTS.document_calc_operation} value={cfg.op || "percent"} options={[{ value: "percent", label: "Percent of base" }, { value: "fixed", label: "Fixed amount" }]} onChange={(op) => setConfig({ op: op as "percent" | "fixed" })} />
          {cfg.op === "fixed" ? (
            <EditInput label="Amount" type="number" value={String(cfg.amount ?? 0)} onChange={(amount) => setConfig({ amount })} />
          ) : (
            <>
              <EditInput label="Rate (%)" type="number" value={String(cfg.rate ?? 0)} onChange={(rate) => setConfig({ rate })} />
              <EditInput label="Base" tooltip={FIELD_HINTS.document_calc_base} value={cfg.base || "subtotal"} options={["subtotal", "total", "tax_total", "discount_total"]} onChange={(base) => setConfig({ base: base as NonNullable<DocumentElement["config"]>["base"] })} />
            </>
          )}
          <EditInput
            label="Apply as"
            tooltip={FIELD_HINTS.document_calc_apply_as}
            value={cfg.apply_as || "discount"}
            options={[
              { value: "discount", label: "Discount (reduces total)" },
              { value: "tax", label: "Tax (adds to total)" },
              { value: "surcharge", label: "Surcharge (adds to total)" },
              { value: "display", label: "Display only" },
            ]}
            onChange={(apply_as) => setConfig({ apply_as: apply_as as NonNullable<DocumentElement["config"]>["apply_as"] })}
          />
          <p><small>Example: a 20% {kind} discount is Percent of base, rate 20, apply as Discount.</small></p>
        </>
      )}
      {element.type === "extra_line" && (
        <>
          <EditInput label="Description" value={cfg.description || ""} onChange={(description) => setConfig({ description, label: description || cfg.label })} />
          <EditInput label="Quantity" type="number" value={String(cfg.quantity ?? 1)} onChange={(quantity) => setConfig({ quantity })} />
          <EditInput label="Price mode" value={cfg.op || "fixed"} options={[{ value: "fixed", label: "Fixed unit price" }, { value: "percent", label: "Percent of subtotal" }]} onChange={(op) => setConfig({ op: op as "percent" | "fixed" })} />
          {cfg.op === "percent" ? (
            <EditInput label="Rate (%)" type="number" value={String(cfg.rate ?? 0)} onChange={(rate) => setConfig({ rate })} />
          ) : (
            <EditInput label="Unit price" type="number" value={String(cfg.unit_price ?? 0)} onChange={(unit_price) => setConfig({ unit_price })} />
          )}
          <p><small>Example: a delivery charge is an extra line with description Delivery and a unit price.</small></p>
        </>
      )}
      <EditInput label="Row" type="number" min={1} max={40} value={element.canvas_row} onChange={(canvas_row) => onChange((current) => ({ ...current, canvas_row: Math.max(1, Number(canvas_row) || 1) }))} />
      <PositionGrid
        colStart={element.col_start}
        colSpan={element.col_span}
        onChange={({ colStart, colSpan }) => onChange((current) => ({ ...current, col_start: colStart, col_span: colSpan }))}
      />
    </>
  );
}
